import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonGrid,
  IonRouterLink,
  IonRow,
  IonText,
} from '@ionic/react';
import { ReactElement, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import {
  PlanEntitlementKey,
  User,
  useRequestPlanFeatureMutation,
} from '../../api/graphql/generated';
import { useCurrentUser } from '../../api/user.hooks';
import {
  trackPlanFeatureLearnMore,
  trackPlanFeqtureRequested,
  trackPlanPaywallPresented,
} from '../../config/analytics';
import { useBeaconRouter } from '../../route/route.hooks';
import { LoadingButton } from '../button/loading-button';
import { IntercomHelp } from '../help/intercom-help';
import { PlanFeatureDetails } from '../plan/plan.types';
import { SaveStatusToast } from '../toast/toast.gql';
import './paywall.css';

interface PaywallProps {
  user: User;
  planEntitlementKey: PlanEntitlementKey;
  onBeforeNavigate?: () => void;
}

interface PaywallSubscriptionDetailProps {
  planEntitlementKey: PlanEntitlementKey;
  onBeforeNavigate?: () => void;
}

const Paywall = (props: PaywallProps): ReactElement => {
  const { user, planEntitlementKey, onBeforeNavigate } = props;
  const { i18n } = useTranslation();

  useEffect(() => {
    trackPlanPaywallPresented({
      isBillingManager: user.billingManager,
      planEntitlementKey,
    });
  }, [planEntitlementKey, user.billingManager]);

  const details = PlanFeatureDetails[planEntitlementKey];

  const title: string = details?.titleKey
    ? i18n.t(details.titleKey)
    : i18n.t('common:unknown');
  const description: string = details?.description
    ? i18n.t(details.description)
    : i18n.t('common:unknown');

  return (
    <div className="paywall-content">
      <IonCard>
        <IonCardHeader>
          <IonCardTitle>{title}</IonCardTitle>
        </IonCardHeader>
        <IonCardContent className="paywall-feature-description">
          {description}
          {details?.img && (
            <div>
              <img src={details.img} />
            </div>
          )}
          {details?.articleId && (
            <IntercomHelp
              articleId={details.articleId}
              description={i18n.t('plan:paywall_learn_more', {
                featureName: title.toLowerCase(),
              })}
              onLaunchArticle={() => {
                trackPlanFeatureLearnMore({
                  isBillingManager: user.billingManager,
                  planEntitlementKey,
                });
              }}
            />
          )}
        </IonCardContent>
      </IonCard>
      <IonCard>
        <IonCardHeader>
          <IonCardTitle>{i18n.t('plan:paywall_need_question')}</IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <PaywallSubscriptionDetail
            planEntitlementKey={planEntitlementKey}
            onBeforeNavigate={onBeforeNavigate}
          />
        </IonCardContent>
      </IonCard>
    </div>
  );
};

const PaywallSubscriptionDetail = (props: PaywallSubscriptionDetailProps) => {
  const { onBeforeNavigate, planEntitlementKey } = props;
  const { i18n } = useTranslation();
  const { builder } = useBeaconRouter();
  const { replace } = useHistory();

  const user = useCurrentUser();

  const [
    requestPlanFeature,
    { loading: requestFeatureLoading, error: requestFeatureError },
  ] = useRequestPlanFeatureMutation();

  const requestFeature = useCallback(
    (planEntitlementKey: PlanEntitlementKey) => {
      trackPlanFeqtureRequested({
        planEntitlementKey,
      });
      requestPlanFeature({
        variables: {
          input: {
            key: planEntitlementKey,
          },
        },
      });
    },
    [requestPlanFeature]
  );

  return (
    <>
      <IonGrid>
        <IonRow>
          <IonText>
            {user?.billingManager
              ? i18n.t('plan:paywall_access_details_billing_manager')
              : i18n.t('plan:paywall_access_details_non_billing_manager')}
          </IonText>
        </IonRow>
        <IonRow>
          <div className="ion-padding ion-text-center">
            {user?.billingManager ? (
              <IonRouterLink
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (typeof onBeforeNavigate === 'function') {
                    onBeforeNavigate();
                  }

                  // replace current route with billing route. this is to ensure
                  // we don't have a back button to the paywall and that current
                  // page component is replaced with billing page component
                  // allowing for proper closing and unmounting
                  // (i.e, bottom panel)
                  replace(builder.getBillingRoute());
                }}
              >
                {i18n.t('common:go_to_page', {
                  pageName: i18n.t('common:billing'),
                })}
              </IonRouterLink>
            ) : (
              <>
                <LoadingButton
                  loading={requestFeatureLoading ?? false}
                  buttonProps={{
                    onClick: () => requestFeature(planEntitlementKey),
                  }}
                >
                  {i18n.t('common:send_request')}
                </LoadingButton>
              </>
            )}
          </div>
        </IonRow>
      </IonGrid>
      <SaveStatusToast
        loading={requestFeatureLoading}
        error={requestFeatureError}
        defaultMessageKey={'plan:plan_update_request_sent'}
        defaultErrorMessageKey={'plan:plan_update_request_failed'}
      />
    </>
  );
};

export { Paywall, PaywallSubscriptionDetail };
